import { Form as FormikForm, Formik, FormikErrors, FormikProps, FormikTouched } from 'formik';
import React, { useEffect, useMemo, useState } from 'react';
import { Col, Row, Stack } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import * as Yup from 'yup';
import { BackButton } from '../../../../../components/buttons/BackButton';
import { SimpleButton } from '../../../../../components/buttons/SimpleButton';
import Stepper from '../../../../../components/stepper/Stepper';
import { useToast } from '../../../../../context/ToastContext';
import {
  useCreatePromotionalCodeMutation,
  useGetPromotionalCodeQuery,
  useUpdatePromotionalCodeMutation
} from '../../../../../services/endpoints/orders/promotionalCode';
import { getCurrentUser } from '../../../../../services/helper';
import { getErrorMessage, getObjectDifference } from '../../../../../utils/utils';
import { FadeIn } from '../../../../animations/FadeIn';
import { PromotionalCodeForm } from './PromotionalCodeForm';
import { userCanCreateEditDelete_PromotionalCodes } from './PromotionalCodesView';

const steps = [{ name: 'Details', form: PromotionalCodeForm }];

export interface FormValues {
  name: string;
  code: string;
  percent_discount: number;
  is_active: boolean;
  location_id: { value: number; label: string } | null;
  expiration_date: string;
}

export interface StepProps {
  errors: Partial<FormikErrors<FormValues>>;
  touched: Partial<FormikTouched<FormValues>>;
  values?: FormValues;
  setFieldValue?: (field: string, value: any, shouldValidate?: boolean) => void;
  readOnly?: boolean;
  /** Super/Supreme can pick (or clear) location; local roles hide the field. */
  showLocationField?: boolean;
}

interface AddEditPromotionalCodeProps {
  editing?: boolean;
}

const toLocationOption = (
  locationId?: number | null,
  title?: string
): FormValues['location_id'] => {
  if (!locationId) return null;
  return { value: locationId, label: title ?? '' };
};

const normalizeExpirationDate = (value?: string | null) => {
  if (!value) return '';
  // DATEONLY / ISO → YYYY-MM-DD for <input type="date">
  return value.slice(0, 10);
};

export const AddEditPromotionalCode: React.FC<AddEditPromotionalCodeProps> = ({ editing }) => {
  const params = useParams();
  const [currentStep, setCurrentStep] = useState(0);
  const { addToast } = useToast();
  const navigate = useNavigate();
  const currentUser = getCurrentUser();

  // Super/Supreme may choose or omit location. Local roles (when FE is opened later) get it auto-set.
  const canSelectLocation = !!(currentUser?.isSuper || currentUser?.isSupremeAdmin);
  const lockedLocation = useMemo(
    () => toLocationOption(currentUser?.location_id, currentUser?.location?.title),
    [currentUser?.location_id, currentUser?.location?.title]
  );

  const [createPromoCode] = useCreatePromotionalCodeMutation();
  const [updatePromoCode] = useUpdatePromotionalCodeMutation();
  const { data, error, isLoading } = useGetPromotionalCodeQuery(Number(params.id!), {
    skip: !editing || !params.id
  });

  const [initialValues, setInitialValues] = useState<FormValues>({
    name: '',
    location_id: canSelectLocation ? null : lockedLocation,
    code: '',
    percent_discount: 0,
    is_active: false,
    expiration_date: ''
  });

  const validationSchemas = [
    Yup.object({
      name: Yup.string().required('Name is required'),
      location_id: canSelectLocation
        ? Yup.mixed().nullable().optional()
        : Yup.object({
            value: Yup.number().min(1).required(),
            label: Yup.string().optional()
          }).required(),
      code: Yup.string().required('Code is required'),
      percent_discount: Yup.number()
        .min(1, 'Number should be higher than 0')
        .max(100, 'Number can not exceed 100')
        .required('Percent discount is required'),
      is_active: Yup.boolean().required(),
      expiration_date: Yup.string().nullable().optional()
    })
  ];

  const resolveLocationId = (formValue: FormValues): number | null => {
    if (!canSelectLocation) {
      return currentUser?.location_id ?? null;
    }
    const value = formValue.location_id?.value;
    return value && value > 0 ? value : null;
  };

  const toPayload = (formValue: FormValues) => ({
    ...formValue,
    location_id: resolveLocationId(formValue),
    expiration_date: formValue.expiration_date || null
  });

  useEffect(() => {
    if (editing && error && !isLoading) {
      addToast('Error while loading promotional code', 'error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing, error, isLoading]);

  useEffect(() => {
    if (data && data.promotionalCode) {
      const { promotionalCode } = data;
      setInitialValues({
        name: promotionalCode.name,
        code: promotionalCode.code,
        location_id: canSelectLocation
          ? toLocationOption(promotionalCode.location_id, promotionalCode.location?.title)
          : lockedLocation,
        percent_discount: promotionalCode.percent_discount,
        is_active: promotionalCode.is_active,
        expiration_date: normalizeExpirationDate(promotionalCode.expiration_date)
      });
    }

    // FE create/edit remains Super/Supreme-only for now (API already allows location-scoped roles).
    if (!userCanCreateEditDelete_PromotionalCodes(currentUser)) {
      addToast('You have no permissions to edit this data', 'error');
      navigate(`/admin/orders/promotional-codes`);
    }
  }, [data]);

  const handleSumbit = async (formValue: FormValues) => {
    try {
      const payload = toPayload(formValue);
      if (editing) {
        const formattedDiscount = JSON.parse(JSON.stringify(data?.promotionalCode)) as FormValues;
        const update = getObjectDifference(formattedDiscount, formValue);
        if (Object.keys(update).length > 0) {
          const res = await updatePromoCode({
            id: data?.promotionalCode.id!,
            data: payload
          }).unwrap();
          if (res && res.promotionalCode) {
            addToast(`Promotional code updated succesfully`, 'success');
          } else {
            addToast(`No changes to promotional code`, 'info');
          }
          navigate(`/admin/orders/promotional-codes/${data?.promotionalCode?.id}`);
        }
      } else {
        const res = await createPromoCode(payload).unwrap();
        if (res && res.promotionalCode) {
          navigate(`/admin/orders/promotional-codes/${res.promotionalCode.id}`);
          addToast('Promotional code created succesfully', 'success');
        } else throw new Error('A problem happened while creating promotional code');
      }
    } catch (e) {
      addToast(getErrorMessage(e), 'error');
    }
  };

  const handleNext = async (
    validateForm: () => Promise<FormikErrors<FormValues>>,
    setTouched: (touched: FormikTouched<FormValues>, shouldValidate?: boolean) => void,
    values: FormValues
  ) => {
    const errors = await validateForm();
    if (Object.keys(errors).length === 0) {
      setCurrentStep(currentStep + 1);
    } else {
      setTouched({
        name: true,
        code: true,
        percent_discount: true,
        is_active: true
      });
    }
  };

  return (
    <FadeIn className="p-lg-4">
      <Stack
        gap={3}
        className="py-lg-0 px-4 py-2"
      >
        <div>
          <BackButton
            text={`Back to promotional code${editing ? ' details' : 's'}`}
            color="#2B4E64"
            url={
              editing
                ? `/admin/orders/promotional-codes/${data?.promotionalCode?.id}`
                : '/admin/orders/promotional-codes'
            }
          />
          <h2
            className="d-none d-lg-block text-info fw-bold mb-0"
            style={{ fontSize: '2.25rem' }}
          >
            {editing ? 'Edit' : 'New'} Promotional Code
          </h2>
          {steps.length > 1 && (
            <Stepper
              steps={steps}
              currentStep={currentStep}
              editing={editing === true}
              onStepClick={(step) => setCurrentStep(step)}
            />
          )}
        </div>

        <div
          className="p-lg-5 p-3"
          style={{
            boxShadow: '0 4px 8px -2px rgba(16, 24, 40, .1),  0 4px 4px 0 rgba(0, 0, 0, .25)',
            border: '1px solid #EBEBEB',
            borderRadius: 10
          }}
        >
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchemas[currentStep]}
            onSubmit={handleSumbit}
            enableReinitialize
          >
            {({
              submitForm,
              isSubmitting,
              validateForm,
              touched,
              errors,
              setTouched,
              values,
              setFieldValue
            }: FormikProps<FormValues>) => (
              <FormikForm className="text-start">
                <div style={{ minHeight: '65vh' }}>
                  {currentStep < steps.length &&
                    React.createElement(steps[currentStep]?.form, {
                      errors,
                      touched,
                      values,
                      setFieldValue,
                      showLocationField: canSelectLocation
                    })}
                </div>

                <Row className="justify-content-end mt-3">
                  <Col
                    xs={6}
                    lg={3}
                  >
                    {currentStep !== 0 && (
                      <SimpleButton
                        disabled={isSubmitting}
                        type="button"
                        onClick={() => setCurrentStep(currentStep - 1)}
                        className="w-100"
                        variant="outline-primary"
                      >
                        Back
                      </SimpleButton>
                    )}
                  </Col>

                  <Col
                    xs={6}
                    lg={3}
                  >
                    <SimpleButton
                      disabled={isSubmitting}
                      type="button"
                      onClick={
                        currentStep === steps.length - 1
                          ? submitForm
                          : () => handleNext(validateForm, setTouched, values)
                      }
                      className="w-100"
                      variant="primary"
                    >
                      {currentStep === steps.length - 1 ? 'Finish' : 'Continue'}
                    </SimpleButton>
                  </Col>
                </Row>
              </FormikForm>
            )}
          </Formik>
        </div>
      </Stack>
    </FadeIn>
  );
};
